import { Row, Table, TableCell, TableRow, Text } from "@amzn/meridian";
import meridianColors from "@amzn/meridian-tokens/base/color";
import React, { useEffect, useState } from "react";
import { useAppSelector } from "src/store/store";
import { GroundTruthField, ReviewResult } from "src/store/modelLifeCycleGroundTruthSlice";
import AnswerCells from "./answerCells/answerCells";
import FieldNameCell from "./fieldNameCell/fieldNameCell";
import { showReviewDistribution } from "./helpers";
import ReviewDecisionCells from "./reviewDecisionCells/reviewDecisionCells";
import ReviewHistory from "./reviewHistory/reviewHistory";
import KnowledgeCell from "src/components/shared/groundTruth/reviewTable/knowledgeCell/knowledgeCell";
import AdditionalInputsCell from "src/components/shared/groundTruth/reviewTable/additionalInputsCell/additionalInputsCell";

const ReviewTable = () => {
  const { alias, ldap } = useAppSelector((state) => state.user);
  const {
    selectedGroundTruthData,
    isBlind,
    showLLMAnswerColumn,
    highlightField,
    showUserAnswerColumn,
  } = useAppSelector((state) => state.modelLifeCycleGroundTruthSlice);
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const additionalInputConfigs = selectedModel.metadata?.additional_reviewer_inputs
    ? String(selectedModel.metadata.additional_reviewer_inputs)
        .split(",")
        .map((s) => {
          const [label, placeholder] = s.trim().split("|");
          return { label, placeholder: placeholder || `Enter ${label.toLowerCase()}...` };
        })
    : [];
  const additionalInputsOnDecision = selectedModel.metadata?.additional_inputs_on_decision
    ? String(selectedModel.metadata.additional_inputs_on_decision)
    : null;
  const [randomizedFieldOutputOrders, setRandomizedFieldOutputOrders] = useState<boolean[]>([]);

  useEffect(() => {
    // for each filed, generate a random true/false value, example result list can be [true, false, ...]
    setRandomizedFieldOutputOrders(selectedGroundTruthData.fields.map(() => Math.random() < 0.5));
  }, []);

  const getAnswerHeader = (isBlind: boolean) => {
    if (showUserAnswerColumn && showLLMAnswerColumn) {
      return (
        <>
          <TableCell width={"20%"}>
            <Text type="h200">{isBlind ? "Source 1" : "User Output"}</Text>
          </TableCell>
          <TableCell width={"20%"}>
            <Text type="h200">{isBlind ? "Source 2" : "LLM Output"}</Text>
          </TableCell>
        </>
      );
    } else if (showUserAnswerColumn) {
      return (
        <TableCell width={"20%"}>
          <Text type="h200">User Output</Text>
        </TableCell>
      );
    } else if (showLLMAnswerColumn) {
      return (
        <TableCell width={"20%"}>
          <Text type="h200">LLM Output</Text>
        </TableCell>
      );
    } else {
      return <></>;
    }
  };

  return (
    <Table showDividers={true} fixHeaderRows={true} fixHeaderRowsFullWidth={true} headerRows={1}>
      <TableRow backgroundColor={meridianColors.colorGray50}>
        <TableCell width={"1%"}></TableCell>
        <TableCell width={"20%"}>
          <Text type="h200">Fields</Text>
        </TableCell>
        {getAnswerHeader(isBlind)}

        {showReviewDistribution(selectedGroundTruthData, ldap, alias) && (
          <TableCell width={"30%"} alignmentHorizontal={"start"}>
            <Row>
              <Text type="h200">Submitted Labels</Text>
            </Row>
          </TableCell>
        )}
        <TableCell width={"fill"} alignmentHorizontal={"start"}>
          <Text type="h200">Review Decision</Text>
        </TableCell>
      </TableRow>
      {selectedGroundTruthData.fields
        .map((groundTruthField: GroundTruthField, index: number) => {
          const showBusinessKnowledge =
            groundTruthField.reviewResult === ReviewResult.NEITHER ||
            groundTruthField.reviewResult === ReviewResult.HUMAN_CORRECT;
          const rows = [
            <TableRow
              highlightOnHover
              backgroundColor={
                highlightField === groundTruthField.fieldName
                  ? meridianColors.colorBlue50
                  : "primary"
              }
            >
              <TableCell width={"1%"}></TableCell>
              <TableCell width={"20%"}>
                <FieldNameCell groundTruthField={groundTruthField}></FieldNameCell>
              </TableCell>
              <AnswerCells
                isBlind={isBlind}
                randomizedOrder={randomizedFieldOutputOrders[index]}
                groundTruthField={groundTruthField}
                showUserAnswerColumn={showUserAnswerColumn}
                showLLMAnswerColumn={showLLMAnswerColumn}
              />
              {showReviewDistribution(selectedGroundTruthData, ldap, alias) && (
                <TableCell width={"fill"}>
                  {selectedGroundTruthData.reviewHistory && (
                    <ReviewHistory
                      reviewHistory={selectedGroundTruthData.reviewHistory}
                      fieldName={groundTruthField.fieldName}
                      metadata={selectedGroundTruthData.metadata}
                      showAdditionalInputs={additionalInputConfigs.length > 0}
                    />
                  )}
                </TableCell>
              )}
              <TableCell width={"fill"}>
                <ReviewDecisionCells
                  randomizedOrder={randomizedFieldOutputOrders[index]}
                  groundTruthField={groundTruthField}
                  fieldIndex={index}
                />
              </TableCell>
            </TableRow>,
          ];
          if (additionalInputConfigs.length > 0) {
            const confirmedAnswer = Array.isArray(groundTruthField.confirmedAnswer)
              ? groundTruthField.confirmedAnswer.join("|")
              : groundTruthField.confirmedAnswer;
            if (additionalInputsOnDecision && confirmedAnswer === additionalInputsOnDecision) {
              rows.push(
                <TableRow backgroundColor="secondary" highlightOnHover>
                  <AdditionalInputsCell index={index} inputConfigs={additionalInputConfigs} />
                </TableRow>,
              );
            }
          } else if (showBusinessKnowledge) {
            rows.push(
              <TableRow backgroundColor="secondary" highlightOnHover>
                <KnowledgeCell groundTruthField={groundTruthField} index={index} />
              </TableRow>,
            );
          }
          return rows;
        })
        .reduce((flattenedRows, rowArray) => flattenedRows.concat(rowArray), [])}
    </Table>
  );
};

export default ReviewTable;
